/**
 * Anchor: a point the rope can attach to. May optionally follow a polyline
 * `path` (see {@link PathSpec}) — moving anchors create dynamic swing puzzles
 * without changing how the player interacts with them.
 *
 * The dashed range ring is rendered around the live position; the world-space
 * `x` / `y` fields are mutated each tick by `step()` so Hook code, collision
 * handlers, and editor utilities all see one source of truth.
 */

import { Graphics, Container } from 'pixi.js';
import type { PathSpec } from '../Path.js';
import { pathPosition, isMovingPath } from '../Path.js';

export interface AnchorOptions {
  x: number;
  y: number;
  hookRadius: number;
  /** Optional path: if present (and valid), the anchor moves each frame. */
  path?: PathSpec;
}

export class Anchor {
  /** Live world-space position. Mutable for moving anchors. */
  x: number;
  y: number;
  readonly hookRadius: number;
  readonly view: Container;
  readonly pathView: Graphics;
  /** Animated proximity ring at hookRadius; alpha fades 0..1 with reach. */
  private readonly waves: Graphics;
  /** Outer dashed ring, used as the highlight when the anchor is targeted. */
  private readonly ring: Graphics;
  /** Pulsing accent ring around the knob; only visible when this anchor is
   *  the nearest in-range candidate (the one that would attach on tap). */
  private readonly pulse: Graphics;
  /** Per-anchor color palette + orbital config, derived from a position seed. */
  private readonly system: SolarSystem;
  private inRange = false;
  private nearest = false;
  /** Eased 0..1 "is currently the nearest pick" weight. Drives pulse alpha
   *  separately from the per-frame cycle multiplier so the easing target
   *  isn't disturbed by the visible animation. */
  private nearestFade = 0;
  private readonly path: PathSpec | undefined;
  private readonly originOffsetX: number;
  private readonly originOffsetY: number;

  constructor(opts: AnchorOptions) {
    this.hookRadius = opts.hookRadius;
    this.path = isMovingPath(opts.path) ? opts.path : undefined;
    // Build a stable per-anchor solar system from the spawn position so
    // the same anchor always looks the same across reloads & replays.
    this.system = makeSolarSystem(opts.x, opts.y, this.hookRadius, !!this.path);

    if (this.path) {
      this.originOffsetX = opts.x - this.path.points[0]!.x;
      this.originOffsetY = opts.y - this.path.points[0]!.y;
      const start = pathPosition(this.path, 0);
      this.x = start.x + this.originOffsetX;
      this.y = start.y + this.originOffsetY;
    } else {
      this.originOffsetX = 0;
      this.originOffsetY = 0;
      this.x = opts.x;
      this.y = opts.y;
    }

    const c = new Container();
    c.x = this.x;
    c.y = this.y;

    // Soft halo right around the knob — always faintly visible. Just a
    // few stacked circles for a smooth radial fade. Identity colour, no
    // animation. Lets the eye find anchors without dominating the screen.
    const aura = new Graphics();
    this.drawAura(aura);
    c.addChild(aura);

    // Reach indicator: a single thin circle at hookRadius. Hidden by
    // default; fades in only when the player is within reach. This is
    // the *only* moving part — it's the proximity signal.
    const waves = new Graphics();
    this.waves = waves;
    waves.alpha = 0;
    c.addChild(waves);

    // Outer dashed ring kept as the explicit "targeted" highlight; it
    // pops above the aura when the player aims at this anchor.
    const ring = new Graphics();
    const segments = 32;
    const stepAng = (Math.PI * 2) / segments;
    for (let i = 0; i < segments; i += 2) {
      const a0 = i * stepAng;
      const a1 = (i + 1) * stepAng;
      ring
        .moveTo(Math.cos(a0) * this.hookRadius, Math.sin(a0) * this.hookRadius)
        .arc(0, 0, this.hookRadius, a0, a1)
        .stroke({ color: 0xffffff, width: 2, alpha: 0.85 });
    }
    c.addChild(ring);
    this.ring = ring;
    ring.visible = false;
    // Subtle nearest-anchor hint: a slightly brighter accent ring around
    // the knob that fades in when this anchor is the closest in-range
    // pick. No animation — just a quiet color change.
    const highlight = new Graphics();
    highlight.circle(0, 0, 9).stroke({ color: this.system.accent, width: 2, alpha: 0.95 });
    highlight.alpha = 0;
    this.pulse = highlight;
    c.addChild(highlight);
    // Anchor knob (the "sun" at the centre). Small, tinted with the
    // system's primary so each anchor still has identity at rest.
    const knob = new Graphics();
    knob.circle(0, 0, 6).fill(0x1a0f28).stroke({ color: this.system.primary, width: 1.5, alpha: 0.9 });
    knob.circle(0, 0, 2).fill(this.system.accent);
    c.addChild(knob);

    this.view = c;

    // Path breadcrumb (rendered in a background layer by the scene).
    const pv = new Graphics();
    if (this.path) {
      const pts = this.path.points;
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1]!, b = pts[i]!;
        const dx = b.x - a.x, dy = b.y - a.y;
        const L = Math.hypot(dx, dy);
        if (L < 0.001) continue;
        const ux = dx / L, uy = dy / L;
        const period = 10;
        let t = 0;
        while (t < L) {
          const t2 = Math.min(t + 4, L);
          pv.moveTo(a.x + ux * t + this.originOffsetX, a.y + uy * t + this.originOffsetY)
            .lineTo(a.x + ux * t2 + this.originOffsetX, a.y + uy * t2 + this.originOffsetY)
            .stroke({ color: 0xaa77dd, width: 2, alpha: 0.35 });
          t += period;
        }
      }
    }
    this.pathView = pv;
  }

  /** Advance the anchor to its position at level-time `tSec`, and refresh
   *  the slow LFO-driven aura + wave animation. Position update is a no-op
   *  for static anchors; the visual breathing runs on every anchor. */
  step(tSec: number): void {
    if (this.path) {
      const p = pathPosition(this.path, tSec);
      this.x = p.x + this.originOffsetX;
      this.y = p.y + this.originOffsetY;
      this.view.x = this.x;
      this.view.y = this.y;
    }
    // Smoothly fade the proximity ring toward its target alpha. Driven
    // here (rather than in setInRange) so the fade plays out across
    // multiple frames even when the in-range state flips just once.
    const wavesTarget = this.inRange ? 1 : 0;
    const wk = 0.18;                             // ~12-frame fade @ 60Hz
    this.waves.alpha += (wavesTarget - this.waves.alpha) * wk;

    // Nearest-anchor highlight: just an alpha fade on a static accent
    // ring around the knob. Subtle color hint, no motion.
    const fadeTarget = this.nearest ? 1 : 0;
    this.nearestFade += (fadeTarget - this.nearestFade) * 0.2;
    this.pulse.alpha = this.nearestFade;
  }

  setHighlighted(on: boolean): void {
    this.ring.visible = on;
  }

  setInRange(on: boolean): void {
    if (this.inRange === on) return;
    this.inRange = on;
    if (on) {
      // Lazily paint the proximity ring the first time we need it. It's
      // a single thin circle at hookRadius in the system's accent
      // colour \u2014 the only feedback the player needs to know they can
      // grab. Fade-in is driven each step().
      const g = this.waves;
      g.clear();
      g.circle(0, 0, this.hookRadius).stroke({ color: this.system.accent, width: 1.2, alpha: 0.7 });
    }
    if (!on) this.nearest = false;
  }

  /** Mark this anchor as the closest in-range candidate (i.e. the one
   *  the hook would attach to right now). Drives a pulsing accent ring
   *  + brightens the proximity circle. */
  setNearest(on: boolean): void {
    this.nearest = on && this.inRange;
  }

  /** Static soft halo: 4 concentric stroked circles inside a small radius
   *  around the knob, alpha fading outward. No animation, no full-reach
   *  gradient \u2014 just enough to make each anchor visible against busy
   *  backgrounds. */
  private drawAura(g: Graphics): void {
    g.clear();
    const color = this.system.primary;
    const maxR = Math.min(this.hookRadius * 0.18, 22);
    const rings = 4;
    for (let i = 1; i <= rings; i++) {
      const t = i / rings;
      const r = maxR * t;
      const a = (1 - t) * 0.22;
      g.circle(0, 0, r).stroke({ color, width: 1, alpha: a });
    }
    // Tiny inner fill for warmth.
    g.circle(0, 0, maxR * 0.4).fill({ color, alpha: 0.10 });
  }

  /** True if this anchor follows a path. */
  get isMoving(): boolean {
    return this.path !== undefined;
  }
}

// ---------------------------------------------------------------------------
// Solar-system palette + orbital config
// ---------------------------------------------------------------------------

interface SolarSystem {
  /** Dominant identity colour (knob stroke + halo). */
  primary: number;
  /** Secondary colour used for the proximity ring at hookRadius. */
  accent: number;
}

/** Tiny mulberry32 PRNG; deterministic, fast, plenty for visuals. */
function rngFromSeed(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** HSL → 0xRRGGBB. h in [0,1), s/l in [0,1]. */
function hsl(h: number, s: number, l: number): number {
  const f = (n: number): number => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(v * 255);
  };
  return (f(0) << 16) | (f(8) << 8) | f(4);
}

/** Build a stable, position-seeded sunset palette. Each anchor samples a
 *  hue in the deep-magenta \u2192 pink \u2192 orange \u2192 gold band so it has its
 *  own warm identity colour while sharing the overall mood. */
function makeSolarSystem(x: number, y: number, _hookRadius: number, moving: boolean): SolarSystem {
  // Hash position into a 32-bit seed (cheap djb2-ish).
  const seed = ((Math.round(x) * 73856093) ^ (Math.round(y) * 19349663)) >>> 0;
  const rand = rngFromSeed(seed || 1);

  // Sunset hue band: 0.92 (deep magenta) -> 0.07 (gold), wrapping past 1.0.
  const t = rand();
  const baseHue = (0.92 + t * 0.15) % 1;
  // Moving anchors sit a bit deeper in the magenta end so type stays readable.
  const hue = moving ? (0.90 + rand() * 0.05) % 1 : baseHue;
  // Accent is a small step *toward* gold along the band \u2014 keeps the
  // primary/accent pair feeling like one gradient, not two colours.
  const accentHue = (hue + 0.04 + rand() * 0.03) % 1;
  const primary = hsl(hue,       0.55, 0.60);
  const accent  = hsl(accentHue, 0.70, 0.68);
  return { primary, accent };
}
